// =============================================================================
// Organization Sidebar - Sidebar pour le module Organisation
// Navigation: Vue d'ensemble, Membres, Invitations, Rôles, Guide Permissions, 
//             Journal d'Audit, Paramètres
// =============================================================================

import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  ChevronLeft, 
  ChevronRight,
  LayoutDashboard,
  Users,
  UserPlus,
  Shield,
  HelpCircle,
  FileText,
  Settings,
} from 'lucide-react';
import { useOrganization } from '@/contexts/OrganizationContext';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface OrganizationSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

const NAV_ITEMS = [
  { 
    path: '/admin/access', 
    icon: LayoutDashboard, 
    label: 'Vue d\'ensemble',
    description: 'Aperçu du workspace'
  },
  { 
    path: '/admin/access?tab=members', 
    icon: Users, 
    label: 'Membres',
    description: 'Gérer les membres',
    matchPath: '/admin/access',
    matchParam: 'tab=members'
  },
  { 
    path: '/admin/access?tab=invitations', 
    icon: UserPlus, 
    label: 'Invitations',
    description: 'Gérer les invitations',
    matchPath: '/admin/access',
    matchParam: 'tab=invitations'
  },
  { 
    path: '/admin/access?tab=roles', 
    icon: Shield, 
    label: 'Rôles',
    description: 'Gérer les rôles',
    matchPath: '/admin/access',
    matchParam: 'tab=roles'
  },
  { 
    path: '/permissions-guide', 
    icon: HelpCircle, 
    label: 'Guide Permissions',
    description: 'Documentation des permissions'
  },
  { 
    path: '/admin/audit-logs', 
    icon: FileText, 
    label: 'Journal d\'Audit',
    description: 'Historique des actions'
  },
  { 
    path: '/admin/settings', 
    icon: Settings, 
    label: 'Paramètres',
    description: 'Paramètres du workspace'
  },
];

// Colors
const COLORS = {
  bg: '#FFFFFF',
  bgHover: '#FEF2F2',
  bgActive: '#FEE2E2',
  border: '#E2E8F0',
  textPrimary: '#0F172A',
  textSecondary: '#64748B',
  accent: '#DC2626', // Modern Red for admin module
};

export function OrganizationSidebar({ collapsed, onToggle }: OrganizationSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();

  const isActive = (item: typeof NAV_ITEMS[0]) => {
    // Check for tab parameter match
    if (item.matchParam) {
      return location.pathname === item.matchPath && location.search.includes(item.matchParam);
    }
    // For overview (no tab param), check exact path without any tab params
    if (item.path === '/admin/access') {
      return location.pathname === '/admin/access' && !location.search.includes('tab=');
    }
    return location.pathname === item.path;
  };

  // Handle navigation - force navigation even on same base path
  const handleNavigation = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    // Use replace: false to ensure navigation happens even on same base path
    navigate(path, { replace: false });
  };

  // ─────────────────────────────────────────────────────────────────────────
  // COLLAPSED MODE
  // ─────────────────────────────────────────────────────────────────────────
  if (collapsed) {
    return (
      <aside 
        className="flex flex-col w-12 shrink-0 border rounded-xl h-full"
        style={{ backgroundColor: COLORS.bg, borderColor: COLORS.border }}
      >
        {/* Header with expand button */}
        <div 
          className="h-14 flex items-center justify-center border-b"
          style={{ borderColor: COLORS.border }}
        >
          <Button 
            variant="ghost" 
            size="icon" 
            className="w-8 h-8"
            style={{ color: COLORS.textSecondary }}
            onClick={onToggle}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Nav Icons */}
        <ScrollArea className="flex-1">
          <div className="flex flex-col items-center py-3 gap-1">
            {NAV_ITEMS.map((item) => {
              if (!item?.icon) return null;
              const Icon = item.icon;
              const active = isActive(item);

              return (
                <Tooltip key={item.path}>
                  <TooltipTrigger asChild>
                    <a
                      href={item.path}
                      onClick={(e) => handleNavigation(e, item.path)}
                      className="w-9 h-9 rounded-lg flex items-center justify-center transition-colors cursor-pointer"
                      style={{
                        backgroundColor: active ? COLORS.bgActive : 'transparent',
                      }}
                      onMouseEnter={(e) => {
                        if (!active) {
                          e.currentTarget.style.backgroundColor = COLORS.bgHover;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!active) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <Icon 
                        className="h-4 w-4" 
                        style={{ 
                          color: active ? COLORS.accent : COLORS.textSecondary,
                          strokeWidth: active ? 2.5 : 2
                        }} 
                      />
                    </a>
                  </TooltipTrigger>
                  <TooltipContent 
                    side="right"
                    style={{ 
                      backgroundColor: '#1E293B', 
                      color: '#FFFFFF', 
                      borderColor: '#334155' 
                    }}
                  >
                    {item.label}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        </ScrollArea>
      </aside>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // EXPANDED MODE
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <aside 
      className="flex flex-col w-56 shrink-0 border rounded-xl h-full"
      style={{ backgroundColor: COLORS.bg, borderColor: COLORS.border }}
    >
      {/* Header */}
      <div 
        className="h-14 flex items-center justify-between px-4 border-b"
        style={{ borderColor: COLORS.border }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Settings className="h-4 w-4 shrink-0" style={{ color: COLORS.accent }} />
          <span 
            className="font-semibold text-sm truncate"
            style={{ color: COLORS.textPrimary }}
          >
            Workspace
          </span>
        </div>
        <Button 
          variant="ghost" 
          size="icon" 
          className="w-7 h-7 shrink-0"
          style={{ color: COLORS.textSecondary }}
          onClick={onToggle}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1">
        <nav className="p-2 space-y-1">
          {NAV_ITEMS.map((item) => {
            if (!item?.icon) return null;
            const Icon = item.icon;
            const active = isActive(item);

            return (
              <a
                key={item.path}
                href={item.path}
                onClick={(e) => handleNavigation(e, item.path)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer",
                  "hover:bg-[#F1F5F9]"
                )}
                style={{
                  backgroundColor: active ? COLORS.bgActive : 'transparent',
                  color: active ? COLORS.accent : COLORS.textPrimary,
                  fontWeight: active ? 500 : 400,
                }}
              >
                <Icon 
                  className="h-4 w-4 shrink-0" 
                  style={{ 
                    color: active ? COLORS.accent : COLORS.textSecondary,
                    strokeWidth: active ? 2.5 : 2
                  }} 
                />
                <span className="truncate">{item.label}</span>
              </a>
            );
          })}
        </nav>
      </ScrollArea>

      {/* Footer - Organization info */}
      {currentOrganization && (
        <div 
          className="p-3 border-t"
          style={{ borderColor: COLORS.border }}
        >
          <p 
            className="text-xs truncate"
            style={{ color: COLORS.textSecondary }}
          >
            Membres, rôles et accès
          </p>
        </div>
      )}
    </aside>
  );
}
